import { createClient, SupabaseClient, Session, User } from '@supabase/supabase-js';

/**
 * Authentication state interface
 */
export interface AuthState {
    isAuthenticated: boolean;
    user: User | null;
    session: Session | null;
}

/**
 * Supabase authentication provider
 * Manages user authentication for admin panel access
 */
export class SupabaseAuthProvider {
    private static instance: SupabaseAuthProvider;
    private supabase: SupabaseClient;
    private authStateListeners: Set<(state: AuthState) => void> = new Set();
    private currentState: AuthState = {
        isAuthenticated: false,
        user: null,
        session: null
    };

    private constructor() {
        // Initialize Supabase client with environment variables
        const supabaseUrl = import.meta.env.VITE_MOKUNET_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_MOKUNET_SUPABASE_ANON_KEY;

        if (!supabaseUrl || !supabaseAnonKey) {
            throw new Error('Supabase credentials not configured. Please set VITE_MOKUNET_SUPABASE_URL and VITE_MOKUNET_SUPABASE_ANON_KEY');
        }

        this.supabase = createClient(supabaseUrl, supabaseAnonKey);

        // Set up auth state listener
        this.supabase.auth.onAuthStateChange((event, session) => {
            console.log('Auth state changed:', event);
            this.updateAuthState(session);
        });

        // Initialize auth state
        this.initializeAuthState();
    }

    /**
     * Get singleton instance
     */
    public static getInstance(): SupabaseAuthProvider {
        if (!SupabaseAuthProvider.instance) {
            SupabaseAuthProvider.instance = new SupabaseAuthProvider();
        }
        return SupabaseAuthProvider.instance;
    }

    /**
     * Initialize authentication state
     */
    private async initializeAuthState(): Promise<void> {
        try {
            const { data: { session }, error } = await this.supabase.auth.getSession();
            if (error) throw error;
            this.updateAuthState(session);
        } catch (error) {
            console.error('Failed to initialize auth state:', error);
            this.updateAuthState(null);
        }
    }

    /**
     * Update authentication state and notify listeners
     */
    private updateAuthState(session: Session | null): void {
        this.currentState = {
            isAuthenticated: !!session,
            user: session?.user || null,
            session: session
        };

        // Notify all listeners
        this.authStateListeners.forEach(listener => listener(this.currentState));
    }

    /**
     * Subscribe to auth state changes
     */
    public onAuthStateChange(callback: (state: AuthState) => void): () => void {
        this.authStateListeners.add(callback);
        
        // Immediately call with current state
        callback(this.currentState);

        // Return unsubscribe function
        return () => {
            this.authStateListeners.delete(callback);
        };
    }

    /**
     * Sign in with email and password
     */
    public async signIn(email: string, password: string): Promise<{ success: boolean; error?: string }> {
        try {
            const { error } = await this.supabase.auth.signInWithPassword({
                email,
                password
            });

            if (error) throw error;

            return { success: true };
        } catch (error) {
            console.error('Sign in error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Sign in failed'
            };
        }
    }

    /**
     * Sign out current user
     */
    public async signOut(): Promise<void> {
        try {
            const { error } = await this.supabase.auth.signOut();
            if (error) throw error;
        } catch (error) {
            console.error('Sign out error:', error);
            throw error;
        }
    }

    /**
     * Get current authentication token
     */
    public async getToken(): Promise<string | null> {
        try {
            const { data: { session }, error } = await this.supabase.auth.getSession();
            if (error) throw error;
            return session?.access_token || null;
        } catch (error) {
            console.error('Failed to get token:', error);
            return null;
        }
    }

    /**
     * Refresh current session
     */
    public async refreshSession(): Promise<{ success: boolean; error?: string }> {
        try {
            const { error } = await this.supabase.auth.refreshSession();
            if (error) throw error;

            return { success: true };
        } catch (error) {
            console.error('Session refresh error:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Session refresh failed'
            };
        }
    }

    /**
     * Get current auth state
     */
    public getAuthState(): AuthState {
        return this.currentState;
    }

    /**
     * Check if user is authenticated
     */
    public isAuthenticated(): boolean {
        return this.currentState.isAuthenticated;
    }

    /**
     * Get current user
     */
    public getCurrentUser(): User | null {
        return this.currentState.user;
    }
}
